import { useRouter } from 'next/router';
import { memo, useCallback, useMemo } from 'react';

import { ImportIcon, PauseIcon, RefreshCwIcon } from '@air/design-icons';

import { useImportManagerSession } from '~/components/Import/useImportManagerSession';
import {
  MAIN_SIDE_NAV_SMALL_ICON_SIZE,
  MainSideNavItemButton,
} from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemButton';
import { MainSideNavItemCountBadge } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemCountBadge';
import { MainSideNavItemIcon } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemIcon';
import { MainSideNavItemLabel } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemLabel';
import { useMainSideNavOverlay } from '~/components/v2/components/workspace/main-side-nav/useMainSideNavOverlay';
import { countWorkspaceImports } from '~/components/v2/components/workspace/workspace-imports/buildWorkspaceImportRows';
import { getWorkspaceImportsRail } from '~/components/v2/components/workspace/workspace-imports/getWorkspaceImportsHeader';
import { isSubscriptionCheckoutRoute } from '~/constants/routes';
import { WORKSPACE_IMPORTS_TRAY_ID } from '~/constants/workspaceActivity';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

export const MainSideNavItemImports = memo(() => {
  const router = useRouter();

  const { isOpen, onOpenChange } = useMainSideNavOverlay({ id: 'imports' });

  const { data: subscription } = useSubscriptionContext();
  const { hasImportSession, importerTasks, imports } = useImportManagerSession();

  const counts = useMemo(
    () => countWorkspaceImports({ localTasks: importerTasks, remoteImports: imports }),
    [importerTasks, imports],
  );
  const isUpgradingPlan = isSubscriptionCheckoutRoute(router.pathname);
  const isUpgradeSuccess = isUpgradingPlan && subscription?.plan === router.query.planId;
  const { rail } = useMemo(
    () =>
      getWorkspaceImportsRail({
        counts,
        isInFreePlan: subscription?.isInFreePlan ?? false,
        isUpgradeSuccess,
        isUpgradingPlan,
      }),
    [counts, isUpgradeSuccess, isUpgradingPlan, subscription?.isInFreePlan],
  );

  const handleClick = useCallback(() => {
    onOpenChange(!isOpen);
  }, [isOpen, onOpenChange]);

  if (!hasImportSession) {
    return null;
  }

  return (
    <MainSideNavItemButton
      aria-controls={WORKSPACE_IMPORTS_TRAY_ID}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      aria-label={rail.ariaLabel}
      data-item-id="imports"
      data-main-side-nav-overlay
      data-state={isOpen ? 'open' : undefined}
      data-testid="WORKSPACE_IMPORTS_BUTTON"
      onClick={handleClick}
      type="button"
    >
      <MainSideNavItemIcon>
        <span className="relative" data-finished-batch-offer-anchor="">
          {rail.icon === 'stopped' ? <PauseIcon className="shrink-0" size={MAIN_SIDE_NAV_SMALL_ICON_SIZE} /> : null}
          {rail.icon === 'spinner' ? (
            <RefreshCwIcon
              className="shrink-0 animate-spin motion-reduce:animate-none"
              size={MAIN_SIDE_NAV_SMALL_ICON_SIZE}
            />
          ) : null}
          {rail.icon === 'default' || rail.icon === 'complete' ? (
            <ImportIcon className="shrink-0" size={MAIN_SIDE_NAV_SMALL_ICON_SIZE} />
          ) : null}
          {rail.icon === 'complete' ? (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full ads:bg-ads-grass-9 bg-grass-9"
              data-testid="WORKSPACE_IMPORTS_BUTTON_COMPLETE_DOT"
            />
          ) : null}
          {rail.badge?.kind === 'error' ? (
            <MainSideNavItemCountBadge
              count={rail.badge.count}
              data-testid="WORKSPACE_IMPORTS_BUTTON_ERROR_BADGE"
              tone="warning"
            />
          ) : null}
          {rail.badge?.kind === 'progress' ? (
            <MainSideNavItemCountBadge count={rail.badge.count} data-testid="WORKSPACE_IMPORTS_BUTTON_BADGE" />
          ) : null}
        </span>
      </MainSideNavItemIcon>
      <MainSideNavItemLabel>{rail.label}</MainSideNavItemLabel>
    </MainSideNavItemButton>
  );
});

MainSideNavItemImports.displayName = 'MainSideNavItemImports';
